import React, { useState, useEffect } from 'react'
import { Modal, Text, Group, Button, ScrollArea, Select, Textarea, Divider, Box, MultiSelect } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { updateCountryPayments, getCountryPayments } from '../../../../services/location'
import { getPaymentChannelList } from '../../../../services/payment-channels'
import { getAllStatusesList } from '../../../../services/status'
import { validation } from '../../../../plugins/validation'

const defaultValidation = {
    int_payment_channel: { isError: false, message: '' },
    int_remark: { isError: false, message: '' },
    dom_payment_channel: { isError: false, message: '' },
    dom_remark: { isError: false, message: '' }
}

const ModalDefaultPayment = ({ opened, onClose, countryData, onSuccess }) => {
    const [formData, setFormData] = useState({
        country_id: null,
        international_shipping: {
            payment_channel_ids: [],
            instruction: '',
            status_id: null
        },
        domestic_shipping: {
            payment_channel_ids: [],
            instruction: '',
            status_id: null
        }
    })
    const [submitting, setSubmitting] = useState(false)
    const [paymentChannels, setPaymentChannels] = useState([])
    const [statuses, setStatuses] = useState([])
    const [loadingData, setLoadingData] = useState(false)
    const [validationForm, setValidationForm] = useState(defaultValidation)
    useEffect(() => {
        if (opened) {
            fetchPaymentChannels()
            fetchStatuses()
        }
    }, [opened])

    useEffect(() => {
        if (countryData) {
            setFormData({
                country_id: countryData.id,
                international_shipping: {
                    payment_channel_ids: [],
                    instruction: '',
                    status_id: null
                },
                domestic_shipping: {
                    payment_channel_ids: [],
                    instruction: '',
                    status_id: null
                }
            })
            fetchExistingPayments()
        }
    }, [countryData])

    const fetchExistingPayments = async () => {
        try {
            setLoadingData(true)
            const response = await getCountryPayments({
                'filter[country_id][is]': countryData.id,
                'page[limit]': 100 // Ensure we get all records
            })

            if (response.success && response.data) {
                const payments = response.data

                const internationalIds = []
                let internationalStatus = null
                let internationalInstruction = ''

                const domesticIds = []
                let domesticStatus = null
                let domesticInstruction = ''

                payments.forEach(payment => {
                    const attr = payment.attributes
                    if (attr.shipping_type === 1) { // International
                        internationalIds.push(String(attr.payment_channel_id))
                        // Assuming status and instruction are same for all channels in this group
                        if (!internationalStatus) internationalStatus = attr.status_id ? String(attr.status_id) : null
                        if (!internationalInstruction) internationalInstruction = attr.instruction || ''
                    } else if (attr.shipping_type === 2) { // Domestic
                        domesticIds.push(String(attr.payment_channel_id))
                        if (!domesticStatus) domesticStatus = attr.status_id ? String(attr.status_id) : null
                        if (!domesticInstruction) domesticInstruction = attr.instruction || ''
                    }
                })

                setFormData(prev => ({
                    ...prev,
                    international_shipping: {
                        payment_channel_ids: internationalIds,
                        instruction: internationalInstruction,
                        status_id: internationalStatus
                    },
                    domestic_shipping: {
                        payment_channel_ids: domesticIds,
                        instruction: domesticInstruction,
                        status_id: domesticStatus
                    }
                }))
            }
        } catch (error) {
            console.error('Error fetching existing payments:', error)
            notifications.show({
                title: 'Error',
                message: 'Failed to load existing payment settings',
                color: 'red'
            })
        } finally {
            setLoadingData(false)
        }
    }

    const fetchPaymentChannels = async () => {
        const params = {
            'page[limit]': 100
        }
        setLoadingData(true)
        try {
            const response = await getPaymentChannelList({ 'page[limit]': 100 })
            if (response.success && response.data) {
                const channels = response.data.map((item) => ({
                    value: item.id.toString(),
                    label: item.attributes.name
                }))
                setPaymentChannels(channels)
            }
        } catch (error) {
            console.error('Error fetching payment channels:', error)
            notifications.show({
                title: 'Error',
                message: 'Failed to load payment channels',
                color: 'red'
            })
        } finally {
            setLoadingData(false)
        }
    }

    const fetchStatuses = async () => {
        try {
            const params = {
                'filter[status_type][is]': 2,
                'page[num]': 1,
                'page[limit]': 300
            }
            const response = await getAllStatusesList(params)
            if (response.success && response.data) {
                const statusList = response.data.map((item) => ({
                    value: item.id.toString(),
                    label: item.attributes.name
                }))
                setStatuses(statusList)
            }
        } catch (error) {
            console.error('Error fetching statuses:', error)
            notifications.show({
                title: 'Error',
                message: 'Failed to load statuses',
                color: 'red'
            })
        }
    }

    const handleSubmit = async () => {
        setValidationForm(defaultValidation)
        const validateObj = {
            int_payment_channel: formData.international_shipping.payment_channel_ids.length > 0 ? 'valid' : '',
            int_remark: formData.international_shipping.status_id ? formData.international_shipping.status_id.toString() : '',
            dom_payment_channel: formData.domestic_shipping.payment_channel_ids.length > 0 ? 'valid' : '',
            dom_remark: formData.domestic_shipping.status_id ? formData.domestic_shipping.status_id.toString() : ''
        }

        const isError = validation(validateObj, setValidationForm)
        if (isError) {
            notifications.show({
                title: 'Validation',
                message: 'Please fill all required fields',
                color: 'yellow'
            })
            return
        }

        setSubmitting(true)
        try {
            // Convert payment_channel_ids from string to number array
            const payload = {
                country_id: formData.country_id,
                international_shipping: {
                    payment_channel_ids: formData.international_shipping.payment_channel_ids.map(id => parseInt(id)),
                    instruction: formData.international_shipping.instruction,
                    status_id: parseInt(formData.international_shipping.status_id)
                },
                domestic_shipping: {
                    payment_channel_ids: formData.domestic_shipping.payment_channel_ids.map(id => parseInt(id)),
                    instruction: formData.domestic_shipping.instruction,
                    status_id: parseInt(formData.domestic_shipping.status_id)
                }
            }

            await updateCountryPayments(payload)
            notifications.show({
                title: 'Success',
                message: 'Default payment country updated successfully',
                color: 'green'
            })
            onSuccess()
            onClose()
        } catch (error) {
            console.error(error)
            notifications.show({
                title: 'Error',
                message: 'Failed to update default payment country',
                color: 'red'
            })
        } finally {
            setSubmitting(false)
        }
    }

    const handleInternationalChange = (field, value) => {
        setFormData({
            ...formData,
            international_shipping: {
                ...formData.international_shipping,
                [field]: value
            }
        })
        // Clear validation
        if (field === 'payment_channel_ids') {
            setValidationForm(prev => ({ ...prev, int_payment_channel: { isError: false, message: '' } }))
        }
        if (field === 'status_id') {
            setValidationForm(prev => ({ ...prev, int_remark: { isError: false, message: '' } }))
        }
    }

    const handleDomesticChange = (field, value) => {
        setFormData({
            ...formData,
            domestic_shipping: {
                ...formData.domestic_shipping,
                [field]: value
            }
        })
        // Clear validation
        if (field === 'payment_channel_ids') {
            setValidationForm(prev => ({ ...prev, dom_payment_channel: { isError: false, message: '' } }))
        }
        if (field === 'status_id') {
            setValidationForm(prev => ({ ...prev, dom_remark: { isError: false, message: '' } }))
        }
    }

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={<Text fw='bold'>Default Payments Country</Text>}
            centered
            size="lg"
            scrollAreaComponent={ScrollArea.Autosize}
            closeOnClickOutside={false}
        >
            <Box mb="md">
                <Text size="sm" fw={500} mb={4}>Country Name:</Text>
                <Text size="sm" c="dimmed">{countryData?.name || '-'}</Text>
            </Box>

            <Divider my="md" />

            {/* International Shipping Section */}
            <Box mb="xl">
                <Text fw={600} size="sm" mb="md">International Shipping</Text>

                <MultiSelect
                    label="Payment Channel"
                    placeholder="Select payment channel"
                    data={paymentChannels}
                    value={formData.international_shipping.payment_channel_ids}
                    error={validationForm.int_payment_channel.isError ? validationForm.int_payment_channel.message : ''}
                    onChange={(value) => handleInternationalChange('payment_channel_ids', value)}
                    disabled={loadingData}
                    withAsterisk
                    mb="sm"
                    searchable
                    clearable
                />

                <Textarea
                    label="Instruction (optional)"
                    placeholder="Enter instruction"
                    minRows={3}
                    value={formData.international_shipping.instruction}
                    onChange={(e) => handleInternationalChange('instruction', e.target.value)}
                    mb="sm"
                />

                <Select
                    label="Remark"
                    placeholder="Choose Remark"
                    data={statuses}
                    value={formData.international_shipping.status_id}
                    onChange={(value) => handleInternationalChange('status_id', value)}
                    error={validationForm.int_remark.isError ? validationForm.int_remark.message : ''}
                    disabled={loadingData}
                    withAsterisk
                    mb="sm"
                />
            </Box>

            {/* Domestic Shipping Section */}
            <Box mb="xl">
                <Text fw={600} size="sm" mb="md">Domestic Shipping</Text>

                <MultiSelect
                    label="Payment Channel"
                    placeholder="Select payment channel"
                    data={paymentChannels}
                    value={formData.domestic_shipping.payment_channel_ids}
                    onChange={(value) => handleDomesticChange('payment_channel_ids', value)}
                    error={validationForm.dom_payment_channel.isError ? validationForm.dom_payment_channel.message : ''}
                    disabled={loadingData}
                    withAsterisk
                    mb="sm"
                    searchable
                    clearable
                />

                <Textarea
                    label="Instruction (optional)"
                    placeholder="Enter instruction"
                    minRows={3}
                    value={formData.domestic_shipping.instruction}
                    onChange={(e) => handleDomesticChange('instruction', e.target.value)}
                    mb="sm"
                />

                <Select
                    label="Remark"
                    placeholder="Choose Remark"
                    data={statuses}
                    value={formData.domestic_shipping.status_id}
                    onChange={(value) => handleDomesticChange('status_id', value)}
                    error={validationForm.dom_remark.isError ? validationForm.dom_remark.message : ''}
                    disabled={loadingData}
                    withAsterisk
                    mb="sm"
                />
            </Box>

            <Group justify="flex-end" mt="xl">
                <Button variant="default" onClick={onClose}>Cancel</Button>
                <Button onClick={handleSubmit} loading={submitting} color="red">Save now</Button>
            </Group>
        </Modal>
    )
}

export default ModalDefaultPayment
